/**
 * Components Animation Script
 * Uses GSAP with ScrollTrigger, MotionPathPlugin, and DrawSVGPlugin to animate components__item elements
 * along the components__inner-border
 *
 * Each components__inner has its own independent animation that works with its own components__inner-border.
 * The animations are triggered separately based on each inner component's position in the viewport.
 * Items are positioned relative to their specific components__inner-border.
 */

(function() {
	// Debug mode configuration
	// Set to false in production to disable debug logging and visualizations
	// This improves performance and reduces console output in the production environment
	// IMPORTANT: Change to 'false' before deploying to production!
	const DEBUG_MODE = true;

	// Helper: compute normalized gap size based on current path length so that
	// items keep approximately a constant pixel gap along the border regardless of responsive scaling.
	// desiredPx represents the distance along the path (in px) we want between items.
 function getGapSize(pathEl, desiredPx = 120) {
		try {
			if (pathEl && typeof pathEl.getTotalLength === 'function') {
				const len = pathEl.getTotalLength();
				if (len && isFinite(len) && len > 0) {
					return desiredPx / len;
				}
			}
		} catch (e) {
			if (DEBUG_MODE) console.warn('getGapSize(): failed to get path length, using fallback constant', e);
		}
		// Fallback: previous constant tuned to a semicircle of ~π*624.5 length
		return 0.0612;
	}

	// Helper: compute desired pixel gap based on actual item sizes to avoid initial overlaps
	function getDesiredGapPx(items) {
		let maxH = 0;
		try {
			items.forEach((item) => {
				const img = item.querySelector('.components__item-img') || item.querySelector('.components__item-img-wrapper') || item;
				const rect = img.getBoundingClientRect();
				if (rect && rect.height) {
					maxH = Math.max(maxH, rect.height);
				}
			});
		} catch (e) {
			if (DEBUG_MODE) console.warn('getDesiredGapPx(): measurement failed, falling back to default', e);
		}
		const isSmall = (typeof window !== 'undefined') && (window.innerWidth <= 992);
		const margin = isSmall ? 16 : 24; // extra spacing to ensure visual separation
		const computed = maxH > 0 ? (maxH + margin) : 120; // fallback to previous default
		// Never go below 120px to keep pleasant separation on large screens
		return Math.max(120, computed);
	}
	// Initialize when DOM is fully loaded
	document.addEventListener('DOMContentLoaded', function() {
		if (DEBUG_MODE) {
			console.log("Components Animation Script - Initializing");
		}

		// Check if GSAP and required plugins are available
		if (typeof gsap === 'undefined' ||
			typeof ScrollTrigger === 'undefined' ||
			typeof MotionPathPlugin === 'undefined' ||
			typeof DrawSVGPlugin === 'undefined') {
			console.error('GSAP or required plugins are not loaded');
			return;
		}

		// Register the plugins with GSAP
		gsap.registerPlugin(ScrollTrigger, MotionPathPlugin, DrawSVGPlugin);

		// Configure ScrollTrigger
		ScrollTrigger.config({ ignoreMobileResize: true });

		// Debug logging
		if (DEBUG_MODE) {
			// Log plugin availability for debugging
			console.log("GSAP Plugins loaded:", {
				GSAP: gsap.version,
				ScrollTrigger: typeof ScrollTrigger !== 'undefined' ? ScrollTrigger.version || "Available" : "Not available",
				MotionPathPlugin: typeof MotionPathPlugin !== 'undefined' ? "Available" : "Not available",
				DrawSVGPlugin: typeof DrawSVGPlugin !== 'undefined' ? "Available" : "Not available"
			});

			console.log("Animation Configuration:", {
				"Border Animation": "Static dashed border",
				"Items Animation": "MotionPath along circular path upward",
				"First Item Position": "Bottom of circle (0.75 of circular path)",
				"End Position": "Top of circle (0 of circular path)",
				"Animation Trigger": "ScrollTrigger - starts at 85% viewport",
				"Animation Duration": "2.5 seconds with power2.inOut easing",
				"Stagger Effect": "0.3s delay between items"
			});
		}

		// Find all components inner sections on the page
		const componentsInners = document.querySelectorAll('.components__inner');

		if (!componentsInners.length) {
			return;
		}

		// Process each components inner section
		componentsInners.forEach(function(inner, innerIndex) {
			const innerBorder = inner.querySelector('.components__inner-border');
			const borderCircle = innerBorder?.querySelector('#border-circle');
			const borderPath = innerBorder?.querySelector('#border-path');
			const items = inner.querySelectorAll('.components__item');

			if (!innerBorder || !borderCircle || !borderPath || !items.length) {
				console.error('Required elements not found in inner component', innerIndex);
				return;
			}

			// Debug logging for path information
			if (DEBUG_MODE) {
				console.log("Path element:", {
					id: borderPath.id,
					pathLength: borderPath.getTotalLength ? borderPath.getTotalLength() : "Not available",
					d: borderPath.getAttribute("d")
				});
			}

			// Handle debug visualization
			const debugPath = innerBorder.querySelector('#border-path-debug');
			if (debugPath) {
				// Show debug path only in debug mode
				debugPath.style.display = DEBUG_MODE ? 'block' : 'none';
			}

			// Check if this is a mirrored component
			const isMirrored = innerBorder.classList.contains('components__inner-border--mirrored');

			if (DEBUG_MODE) {
				console.log(`Inner component ${innerIndex} is mirrored:`, isMirrored);
			}

			// Position items along the border initially
			// Pass the inner component index to the function for better debugging
			positionItemsOnBorder(innerBorder, borderPath, items, isMirrored, innerIndex);

			// Create a master timeline for this specific inner component's animation
			// Use the inner component itself as the trigger for better independence between animations
			const scrollTriggerConfig = {
				trigger: inner, // Use the inner component itself as the trigger
				start: "top 70%", // Start when the top of the inner component is 70% from the top of the viewport
				end: "bottom 20%", // End when the bottom of the inner component is 20% from the top of the viewport
				scrub: 0.5, // Smooth scrubbing effect with 0.5 second lag for smoother animation
				toggleActions: "play none none reverse" // Play on enter, reverse on leave
			};

			// Add debug callbacks if in debug mode
			if (DEBUG_MODE) {
				scrollTriggerConfig.onEnter = () => console.log(`Inner component ${innerIndex} animation started - entered viewport`);
				scrollTriggerConfig.onLeave = () => console.log(`Inner component ${innerIndex} animation completed - left viewport`);
				scrollTriggerConfig.onEnterBack = () => console.log(`Inner component ${innerIndex} animation reversed - entered viewport again`);
				scrollTriggerConfig.onLeaveBack = () => console.log(`Inner component ${innerIndex} animation reset - left viewport going up`);
			}

			// Add onUpdate callback to update active class based on scroll progress
			// This callback is specific to this inner component and only affects its items
			scrollTriggerConfig.onUpdate = (self) => {
				// Only update items from this specific inner component
				updateActiveItem(items, self.progress, innerIndex);
				if (DEBUG_MODE) {
					console.log(`Inner component ${innerIndex} scroll progress:`, self.progress);
				}
			};

			const masterTimeline = gsap.timeline({
				scrollTrigger: scrollTriggerConfig
			});

			if (DEBUG_MODE) {
				console.log("Animation setup complete for inner component", innerIndex);

				// Log circle information for debugging
				console.log("Circle element:", {
					id: borderCircle.id,
					radius: borderCircle.getAttribute("r"),
					circumference: borderCircle.getAttribute("r") * 2 * Math.PI,
					strokeWidth: borderCircle.getAttribute("stroke-width")
				});
			}

			// Skip border drawing animation since we're using a simple static border
			// Just animate the items

			// Debug logging for items
			if (DEBUG_MODE) {
				console.log("Items to animate:", items.length);
			}

			// 2. Animate each item along the path with MotionPathPlugin
			// Compute a dynamic pixel gap based on actual item sizes to avoid overlaps, then convert to normalized path gap once per inner
				const desiredGapPx = getDesiredGapPx(items);
				const normalizedGap = getGapSize(borderPath, desiredGapPx);
				if (DEBUG_MODE) {
					console.log(`Inner component ${innerIndex} dynamic gap:`, { desiredGapPx, normalizedGap });
				}
				items.forEach((item, index) => {
					// Calculate the starting position
					// <=992px: start near the beginning of the path and travel to the other end
					// >992px: start from the middle and go downward with gap
					const gapSize = normalizedGap;
					const isSmallViewport = (typeof window !== 'undefined') && (window.innerWidth <= 992);
					let startPosition;
					if (isSmallViewport) {
						// Cluster items near the path start (1.0), spaced by gap
						startPosition = 1 - (gapSize * index);
					} else {
						// >992px: start from middle and go downward with gap
						startPosition = 0.5 + (gapSize * index);
					}
					// Clamp startPosition to [0, 1]
					startPosition = Math.max(0, Math.min(1, startPosition));

					// Calculate the ending position (move items to the opposite end of the border)
					const endPosition = 0; // Position 0 is the top/opposite end of the circle

				if (DEBUG_MODE) {
					const formulaStr = isSmallViewport
						? `1 - (${gapSize} * ${index}) = ${startPosition}`
						: `0.5 + (${gapSize} * ${index}) = ${startPosition}`;
					console.log(`Animation for item ${index}:`, {
						isMirrored: isMirrored ? 'mirrored' : 'regular',
						itemIndex: index,
						totalItems: items.length,
						startPosition,
						endPosition,
						calculationFormula: formulaStr,
						message: isSmallViewport
       ? "<=992px: start near path beginning, move to opposite end, then hide (display:none)"
							: (isMirrored
								? "Mirrored component: first at middle then toward bottom (initial order bottom-to-top)"
								: "Regular component: first item at middle, subsequent items toward bottom")
					});
				}

				// Debug logging for each item
				if (DEBUG_MODE) {
					console.log(`Inner component ${innerIndex}, Item ${index} animation:`, {
						element: item.className,
						borderPathId: borderPath.id,
						isMirrored,
						startPosition,
						endPosition,
						delay: index * 0.3
					});
				}

				// Find the image wrapper within this item
				const imgWrapper = item.querySelector('.components__item-img-wrapper');
				// Prefer the actual image element if present to ensure centering of the image itself
				const imgEl = item.querySelector('.components__item-img') || imgWrapper;

				const itemRect = item.getBoundingClientRect();
				const imgRect = imgEl.getBoundingClientRect();
				
				// Calculate the distance from the left edge of the item to the center of the image
				// This ensures the border aligns exactly with the middle of the image along the x-axis
				const offsetX = (imgRect.left - itemRect.left) + (imgRect.width / 2);
				
				// Debug logging for offset calculation
				if (DEBUG_MODE) {
					console.log(`Item ${index} animation positioning:`, {
						itemLeft: itemRect.left,
						imgLeft: imgRect.left,
						imgWidth: imgRect.width,
						offsetX: offsetX,
						message: "This offset centers the image on the border along the x-axis during animation"
					});
				}
				
				// Create animation configuration
				// Calculate alignOrigin to center the image on the path
				// This is the ratio of the distance from left edge to image center, relative to the item width
				const itemWidth = itemRect.width;
				
				// Calculate alignOrigin to center the image on the path for both mirrored and regular components
				let alignX;
				alignX = ((imgRect.left - itemRect.left) + (imgRect.width / 2)) / itemWidth;
				const itemHeight = itemRect.height;
				const alignY = ((imgRect.top - itemRect.top) + (imgRect.height / 2)) / itemHeight;
				
				// Debug logging for alignOrigin calculation
				if (DEBUG_MODE) {
					console.log(`Item ${index} alignOrigin:`, {
						itemWidth,
						itemHeight,
						alignX,
						alignY,
						isMirrored,
						message: "Using calculated alignX/alignY to center the image wrapper on the path"
					});
				}

				// Create animation configuration objects with appropriate settings for regular or mirrored components
				const itemFromConfig = {
					motionPath: {
						path: borderPath,
						align: borderPath,
						alignOrigin: [alignX, alignY], // Align center of image wrapper to path using calculated X/Y
						autoRotate: false, // Don't rotate items
						start: startPosition,
						end: startPosition // Start and end at the same position initially
					},
					y: 0,
					opacity: 1,
					scale: 1,
					// Prevent immediate application of the from-state (like opacity) before the tween's start time
					immediateRender: false
				};

				const itemToConfig = {
					motionPath: {
						path: borderPath,
						align: borderPath,
						alignOrigin: [alignX, alignY], // Keep the image wrapper center aligned on the path
						autoRotate: false,
						start: startPosition, // Keep the same start
						end: endPosition // Move to the end position
					},
					y: 0,
					opacity: 1, // Keep fully visible; no opacity changes per requirement
					scale: 0.7, // Scale down
					duration: 2.5, // Increased duration for smoother movement over longer distance
					ease: "power2.inOut", // Enhanced easing for more natural movement
					delay: index * 0.3 // Increased stagger delay for better separation during longer animation
				};

				// Add debug information about the animation configuration
				if (DEBUG_MODE) {
					console.log(`Animation configuration for item ${index}:`, {
						isMirrored,
						fromConfig: {
							alignOrigin: itemFromConfig.motionPath.alignOrigin,
							start: itemFromConfig.motionPath.start,
							end: itemFromConfig.motionPath.end
						},
						toConfig: {
							alignOrigin: itemToConfig.motionPath.alignOrigin,
							start: itemToConfig.motionPath.start,
							end: itemToConfig.motionPath.end
						}
					});
				}

				// Add debug callbacks if in debug mode
				if (DEBUG_MODE) {
					itemToConfig.onStart = () => console.log(`Item ${index} animation started`);
					itemToConfig.onComplete = () => console.log(`Item ${index} animation completed`);
				}

				// Add to timeline with staggered start times
				masterTimeline.fromTo(item, itemFromConfig, itemToConfig, 0.5); // Start after the border animation
				// For small viewports, hide the item immediately after it reaches the opposite edge of the border
				if (isSmallViewport) {
					// Set display:none at the exact moment the motionPath tween completes; this reverses on scroll back
					masterTimeline.set(item, { display: "none" }, ">");
				}
			});
		});

		// Add debounced resize/orientation handler to recompute positions at responsive breakpoints
		const debounce = (fn, delay = 200) => {
			let t;
			return (...args) => {
				clearTimeout(t);
				t = setTimeout(() => fn.apply(null, args), delay);
			};
		};

		const recalcPositions = debounce(() => {
			if (DEBUG_MODE) {
				console.log('Recalculating component item positions on resize');
			}
			const inners = document.querySelectorAll('.components__inner');
			inners.forEach((inner, innerIndex) => {
				const innerBorder = inner.querySelector('.components__inner-border');
				const borderPath = innerBorder?.querySelector('#border-path');
				const items = inner.querySelectorAll('.components__item');
				if (!innerBorder || !borderPath || !items.length) return;
				const isMirrored = innerBorder.classList.contains('components__inner-border--mirrored');
				positionItemsOnBorder(innerBorder, borderPath, items, isMirrored, innerIndex);
			});
			// Refresh ScrollTrigger to re-calc start/end positions after layout changes
			ScrollTrigger.refresh();
		}, 200);

		window.addEventListener('resize', recalcPositions);
		window.addEventListener('orientationchange', recalcPositions);
	});

	/**
	 * Position items along the circular border using MotionPathPlugin
	 * @param {Element} border - The SVG border element
	 * @param {Element} path - The SVG path element to follow
	 * @param {NodeList} items - The component items to position
	 * @param {boolean} isMirrored - Whether this is a mirrored component
	 * @param {number} innerIndex - The index of the inner component for debugging
	 */
	function positionItemsOnBorder(border, path, items, isMirrored = false, innerIndex = 0) {
		if (!items.length) return;

		// Get the parent container dimensions
		const container = border.closest('.components__inner-img-wrapper');

		// Position each item along the path
		// Compute a dynamic pixel gap based on actual item sizes for this inner, then convert to normalized units once
		const desiredGapPx = getDesiredGapPx(items);
		const normalizedGap = getGapSize(path, desiredGapPx);
		if (DEBUG_MODE) {
			console.log(`Inner component ${innerIndex} dynamic gap (positioning):`, { desiredGapPx, normalizedGap });
		}
		items.forEach((item, index) => {
			// Calculate the position along the path (0 to 1)
			// <=992px: start near the beginning of the path (1.0) and move to the other end
			// >992px: start from the middle and go downward with gap
			const gapSize = normalizedGap;
			const isSmallViewport = (typeof window !== 'undefined') && (window.innerWidth <= 992);
			let startPosition;
			if (isSmallViewport) {
				// Cluster items near the path start (1.0), spaced by gap
				startPosition = 1 - (gapSize * index);
			} else {
				// >992px: start from middle and go downward with gap
				startPosition = 0.5 + (gapSize * index);
			}
			// Clamp startPosition to [0, 1]
			startPosition = Math.max(0, Math.min(1, startPosition));

			if (DEBUG_MODE) {
				const formulaStr = isSmallViewport
					? `1 - (${gapSize} * ${index}) = ${startPosition}`
					: `0.5 + (${gapSize} * ${index}) = ${startPosition}`;
				console.log(`Inner component ${innerIndex}, Item ${index} start position:`, {
					startPosition,
					isMirrored: isMirrored ? 'mirrored' : 'regular',
					itemIndex: index,
					totalItems: items.length,
					calculationFormula: formulaStr,
					pathId: path.id,
					pathD: path.getAttribute("d").substring(0, 30) + "..." // Show part of the path data for verification
				});
			}
			// Find the image wrapper within this item
			const imgWrapper = item.querySelector('.components__item-img-wrapper');

			// First, set a temporary position to get the rects for calculation
			gsap.set(item, {
				motionPath: {
					path: path,
					align: path,
					alignOrigin: [0, 0.5], // Temporary alignment to get proper measurements
					autoRotate: false,
					start: startPosition,
					end: startPosition
				}
			});

			// Get the measurements after initial positioning
			const itemRect = item.getBoundingClientRect();
			// Prefer the actual image element if present to ensure centering of the image itself
			const imgEl = item.querySelector('.components__item-img') || imgWrapper;
			const imgRect = imgEl.getBoundingClientRect();
			
			// Calculate alignOrigin to center the image on the path
			// This is the ratio of the distance from left/top edge to image center, relative to the item width/height
			const itemWidth = itemRect.width;
			const itemHeight = itemRect.height;
			
			// Calculate alignOrigin to center the image on the path for both mirrored and regular components
			let alignX;
			alignX = ((imgRect.left - itemRect.left) + (imgRect.width / 2)) / itemWidth;
			const alignY = ((imgRect.top - itemRect.top) + (imgRect.height / 2)) / itemHeight;
			
			// Debug logging for alignOrigin calculation
			if (DEBUG_MODE) {
				console.log(`Inner component ${innerIndex}, Item ${index} positioning alignOrigin:`, {
					itemWidth,
					itemHeight,
					alignX,
					alignY,
					isMirrored: isMirrored ? 'mirrored' : 'regular',
					pathId: path.id,
					borderElement: border.className,
					message: "Using calculated alignX/alignY to center the image wrapper on the path"
				});
			}

			// Now set the final position with the correct alignOrigin
			// For items except the first one, add a 100px downward shift to prevent overlapping during initialization
			gsap.set(item, {
				motionPath: {
					path: path,
					align: path,
					alignOrigin: [alignX, alignY], // Align center of image wrapper (X/Y) to path
					autoRotate: false, // Don't rotate items
					start: startPosition,
					end: startPosition // Start and end at the same position initially
				},
				y: 0, // Ensure no vertical offset so the item sits exactly on the border
				// Initial visibility: keep fully visible; timeline will hide at the opposite edge on small screens
				opacity: 1
			});

			// Debug logging to verify the final positioning
			if (DEBUG_MODE) {
				console.log(`Inner component ${innerIndex}, Item ${index} final positioning:`, {
					element: item.className,
					borderElement: border.className,
					pathId: path.id,
					isMirrored: isMirrored ? 'mirrored' : 'regular',
					startPosition,
					alignOrigin: [alignX, alignY],
					yOffset: 0, // No Y-axis offset so the item sits exactly on the border
					message: "Item positioned without Y-axis offset to align with the border"
				});

				// Additional debug logging to verify the item's position relative to the image wrapper
				const itemRectAfter = item.getBoundingClientRect();
				const imgWrapperRectAfter = imgWrapper.getBoundingClientRect();

				// Calculate the center point of the image wrapper
				const imgWrapperCenter = imgWrapperRectAfter.left + (imgWrapperRectAfter.width / 2);

				// Calculate the expected path position based on the component type
				const expectedPathPosition = isMirrored
					? itemRectAfter.left + (itemRectAfter.width * 0.5) // For mirrored, path should go through 50% of item width
					: imgWrapperCenter; // For regular, path should go through center of image wrapper

				console.log(`Inner component ${innerIndex}, Item ${index} position verification:`, {
					itemLeft: itemRectAfter.left,
					itemRight: itemRectAfter.right,
					itemWidth: itemRectAfter.width,
					imgWrapperLeft: imgWrapperRectAfter.left,
					imgWrapperRight: imgWrapperRectAfter.right,
					imgWrapperWidth: imgWrapperRectAfter.width,
					imgWrapperCenter: imgWrapperCenter,
					expectedPathPosition: expectedPathPosition,
					isMirrored: isMirrored ? 'mirrored' : 'regular',
					message: isMirrored
						? "Verifying that the mirrored item is centered on the path (alignX=0.5)"
						: "Verifying that the image wrapper is centered on the path (calculated alignX)"
				});
			}

			// Add 'active' class to the first item on page load
			if (index === 0) {
				item.classList.add('active');
				if (DEBUG_MODE) {
					console.log(`Inner component ${innerIndex}, Item ${index} set as active:`, {
						element: item.className,
						borderElement: border.className,
						isMirrored: isMirrored ? 'mirrored' : 'regular',
						message: "First item of this inner component set as active on page load"
					});
				}
			}
		});
	}

	/**
	 * Updates the active item based on scroll progress
	 * @param {NodeList} items - The component items
	 * @param {number} progress - The scroll progress (0 to 1)
	 * @param {number} innerIndex - The index of the inner component for debugging
	 */
	function updateActiveItem(items, progress, innerIndex = 0) {
		if (!items.length) return;

		// Calculate which item should be active based on scroll progress
		// For example, if we have 5 items, we want to divide the progress into 5 segments
		const itemCount = items.length;
		const segmentSize = 1 / itemCount;

		// Calculate the active index based on progress
		// Math.floor(progress / segmentSize) gives us the index of the current segment
		// We need to clamp it to ensure it's within the valid range
		const activeIndex = Math.min(Math.floor(progress / segmentSize), itemCount - 1);

		if (DEBUG_MODE) {
			console.log(`Inner component ${innerIndex}, Active item index: ${activeIndex} (progress: ${progress.toFixed(2)})`);
		}

		// 1) Toggle 'active' class as before
		items.forEach((item, index) => {
			if (index === activeIndex) {
				if (!item.classList.contains('active')) {
					item.classList.add('active');
					if (DEBUG_MODE) {
						console.log(`Inner component ${innerIndex}, Added active class to item ${index}`);
					}
				}
			} else {
				if (item.classList.contains('active')) {
					item.classList.remove('active');
					if (DEBUG_MODE) {
						console.log(`Inner component ${innerIndex}, Removed active class from item ${index}`);
					}
				}
			}
		});

		// 2) Responsive visibility control:
		// For widths <= 992px, do not override visibility; items will be hidden immediately at the opposite edge via timeline.set
		const isSmallOrBelow = (typeof window !== 'undefined') && (window.innerWidth <= 992);
			if (isSmallOrBelow) {
    // Do not override visibility; let the timeline control immediate hiding at the border end
				if (DEBUG_MODE) {
					console.log(`Inner component ${innerIndex}: Skipping visibility overrides at <=992px to allow immediate hiding at the border end.`);
				}
				return;
			}
			// >992px: ensure full visibility (no opacity animations are applied)
			items.forEach((item) => {
				gsap.set(item, { opacity: 1 });
			});
	}
})();